<template lang="pug">
  yz-dialog(v-model="showDialog" persistent scrollable :max-width="getMaxWidth")
    yz-card
      yz-dialog-normal-content.px-4.py-6
        .text-neutral-700
          .yz-medium-body-md.mb-4 Are you sure you want to delete this {{ item }}?
          .yz-regular-body-sm {{ description }}

          // File preview (if fileData is provided)
          .mt-4(v-if="fileCount > 0")
            .file-preview-list(:class="{ 'scrollable': hasMultipleFiles }")
              yz-card.d-flex.pa-2.round-xl.mb-2(v-for="(file, index) in fileData" :key="index" outlined)
                // Thumbnail/icon area
                .flex-grow-0.mr-4
                  yz-img.round-md(v-if="isImage(file)" :width="40" :height="40" :src="getImageSrc(file)" cover)
                  .yz-file-preview__icon-container.round-md.d-flex.align-center.justify-center(v-else :style="thumbnailContainerStyle")
                    yz-icon(:size="18" color="neutral-500") {{ getFileIcon(file) }}

                // File info
                .text-sm.flex-grow-1.d-flex.flex-column.justify-center
                  .font-medium.text-truncate {{ file.filename || file.name }}
                  .yz-regular-body-xs.neutral500--text {{ formatFileSize(file) }}

          slot
      yz-dialog-normal-actions
        .d-flex.align-center.gap-4
          yz-btn(size="lg" text label="Cancel" :disabled="loading" @click="handleCancel")
          yz-btn(size="lg" label="Confirm" color="danger" :loading="loading" @click="handleConfirm")
</template>

<script>
export default {
  props: {
    value: {
      type: Boolean,
      default: false
    },
    item: {
      type: String,
      default: 'item'
    },
    description: {
      type: String,
      default: 'This action will be done immediately and can\'t be undone. Click "Confirm" to proceed'
    },
    maxWidth: {
      type: [String, Number],
      default: '500px'
    },
    fileData: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    showDialog: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    getMaxWidth() {
      return typeof this.maxWidth === 'number' ? `${this.maxWidth}px` : this.maxWidth
    },
    fileCount() {
      return this.fileData?.length || 0
    },
    hasMultipleFiles() {
      return this.fileCount > 1
    },
    thumbnailContainerStyle() {
      return {
        width: '40px',
        height: '40px',
        backgroundColor: 'var(--yz-neutral-200, #f5f5f5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }
    }
  },
  methods: {
    handleConfirm() {
      this.$emit('confirmed')
    },
    handleCancel() {
      this.$emit('canceled')
    },
    handleClose() {
      this.showDialog = false
    },
    isImage(file) {
      if (!file) return false

      // Check if file has blob (preview mode)
      if (file.blob) {
        return file.type && file.type.startsWith('image/')
      }

      // Check file type or rawType (for formatted data)
      if (file.type && file.type.startsWith('image/')) {
        return true
      }

      // Check filename extension for databin files
      if (file.filename) {
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg']
        const extension =
          '.' +
          file.filename
            .split('.')
            .pop()
            .toLowerCase()
        return imageExtensions.includes(extension)
      }

      return false
    },
    getImageSrc(file) {
      // Priority: blob > file URL > fallback
      if (file.blob) {
        return file.blob
      }

      if (typeof file.file === 'string') {
        return file.file
      }

      // If file.file is a File object, create blob URL
      if (file.file && file.file instanceof File) {
        return URL.createObjectURL(file.file)
      }

      return require('@/assets/image_placeholder.png')
    },
    getFileIcon(file) {
      if (!file || !file.filename || !file.name) {
        return '$yzi-file-02'
      }

      const filename = file.filename.toLowerCase()
      const extension = filename.split('.').pop()

      // Document files
      if (['doc', 'docx'].includes(extension)) {
        return 'mdi-file-document-outline'
      }

      // Spreadsheet files
      if (['xls', 'xlsx', 'csv'].includes(extension)) {
        return 'mdi-file-excel'
      }

      // Presentation files
      if (['ppt', 'pptx'].includes(extension)) {
        return 'mdi-file-powerpoint'
      }

      // Archive files
      if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) {
        return 'mdi-folder-zip-outline'
      }

      // Video files
      if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension)) {
        return 'mdi-file-video'
      }

      // Audio files
      if (['mp3', 'wav', 'flac', 'aac', 'ogg'].includes(extension)) {
        return 'mdi-file-music'
      }

      // Text files
      if (['txt', 'md', 'json', 'xml', 'html', 'css', 'js'].includes(extension)) {
        return 'mdi-file-document-outline'
      }

      // Default file icon
      return '$yzi-file-02'
    },
    formatFileSize(file) {
      if (!file) return '0 B'

      if (file.size && typeof file.size === 'string') {
        return file.size
      }

      const bytes = file.size || 0
      if (!bytes || bytes === 0) return '0 B'

      const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
      const i = Math.floor(Math.log(bytes) / Math.log(1024))

      return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i]
    }
  }
}
</script>

<style scoped>
.yz-file-preview__icon-container {
  background-color: var(--yz-neutral-200, #f5f5f5);
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-preview-list.scrollable {
  max-height: 200px;
  overflow-y: auto;
}

.file-preview-list.scrollable::-webkit-scrollbar {
  width: 4px;
}

.file-preview-list.scrollable::-webkit-scrollbar-track {
  background: #eaecf0;
  border-radius: 8px;
}

.file-preview-list.scrollable::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 8px;
}
</style>
